import { useMutation, useQueryClient } from '@tanstack/react-query'
import { licenceStanding } from '@fieldops/shared'
import { useEffect, useRef, useState, type InputHTMLAttributes } from 'react'
import { Button, Input } from '../ui.js'
import { dateInputValue } from '../../lib/licences.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'

/**
 * What the two certification screens share: a box that saves itself, a writer
 * that sends the whole record every time, and the technician card read back out
 * of the endorsement rows it is stored as.
 */

const SAVE_AFTER_MS = 700

/**
 * An input that commits a pause after typing stops, on blur and on Enter.
 *
 * The value is not re-seeded from the server while the box has focus, because a
 * refetch landing mid-word would put back what was there before. A date is held
 * until its year has four real digits: Chrome reports `0002-03-14` on the way to
 * typing 2027, and a pause there would save it.
 */
export function CommitInput({
  value,
  onCommit,
  type = 'text',
  onBlur,
  onKeyDown,
  onFocus,
  ...props
}: {
  value: string
  onCommit: (next: string) => void
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'defaultValue'>) {
  const [draft, setDraft] = useState(value)
  const focused = useRef(false)
  const committed = useRef(value)
  const latest = useRef(value)
  const timer = useRef<number | null>(null)
  const commit = useRef(onCommit)
  commit.current = onCommit

  useEffect(() => {
    committed.current = value
    if (focused.current) return
    latest.current = value
    setDraft(value)
  }, [value])

  const flush = () => {
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = null

    const next = type === 'text' ? latest.current.trim() : latest.current
    if (next === committed.current) return
    if (type === 'date' && next !== '' && Number(next.slice(0, 4)) < 1900) return

    committed.current = next
    commit.current(next)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps -- flush reads refs
  useEffect(() => () => flush(), [])

  return (
    <Input
      {...props}
      type={type}
      value={draft}
      onChange={(event) => {
        latest.current = event.target.value
        setDraft(event.target.value)
        if (timer.current !== null) window.clearTimeout(timer.current)
        timer.current = window.setTimeout(flush, SAVE_AFTER_MS)
      }}
      onFocus={(event) => {
        focused.current = true
        onFocus?.(event)
      }}
      onBlur={(event) => {
        focused.current = false
        flush()
        onBlur?.(event)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') flush()
        onKeyDown?.(event)
      }}
    />
  )
}

/**
 * Writes a record whole, one request at a time, from the last thing typed.
 *
 * A ticket's number and its date are two boxes and one row. Written from the
 * server's copy, a date saved before the number's request came back would put
 * the old number back. So each patch lands on the latest local copy and the
 * whole record is sent, in order.
 */
export function useWriteThrough<T extends object>(
  server: T,
  write: (next: T) => Promise<unknown>,
): (patch: Partial<T>) => Promise<unknown> {
  const latest = useRef(server)
  const pending = useRef(0)
  const chain = useRef<Promise<unknown>>(Promise.resolve())
  const send = useRef(write)
  send.current = write

  const key = JSON.stringify(server)
  useEffect(() => {
    if (pending.current === 0) latest.current = server
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by content
  }, [key])

  return (patch) => {
    const next = { ...latest.current, ...patch }
    latest.current = next
    pending.current += 1

    chain.current = chain.current
      .then(() => send.current(next))
      .catch(() => undefined)
      .finally(() => {
        pending.current -= 1
      })

    return chain.current
  }
}

/* -------------------------------------------------------------------------- */

type CardHolding = {
  reference: string | null
  expiresAt: Date | string | null
  attachment: { id: string; checksum: string | null; filename: string; mimeType?: string | null } | null
  updatedAt?: Date | string
} | null

export type TechnicianCard = {
  /** The endorsements switched on, by licence type id. */
  heldIds: string[]
  reference: string
  /** `yyyy-mm-dd`, or empty. */
  expiresAt: string
  attachment: NonNullable<CardHolding>['attachment']
  /** True where the rows carry different numbers or dates: the first save evens them out. */
  disagree: boolean
  standing: ReturnType<typeof licenceStanding>
}

/**
 * One card, read from the endorsement rows it is stored as.
 *
 * Each row carries the card's number and date (decision 66). The most recently
 * saved row speaks for the card, and `disagree` says when the others do not
 * match it — imported or hand-typed rows can, and the card screen writes them
 * all at once.
 */
export function readCard(
  endorsements: ReadonlyArray<{ licenceType: { id: string; warnDays: number }; holding: CardHolding }>,
  now: Date = new Date(),
): TechnicianCard {
  const held = endorsements
    .filter((entry) => entry.holding !== null)
    .sort(
      (a, b) =>
        new Date(b.holding?.updatedAt ?? 0).getTime() - new Date(a.holding?.updatedAt ?? 0).getTime(),
    )

  const lead = held[0]?.holding ?? null
  const reference = lead?.reference ?? ''
  const expiresAt = dateInputValue(lead?.expiresAt)
  const warnDays = Math.max(0, ...endorsements.map((entry) => entry.licenceType.warnDays))

  return {
    heldIds: held.map((entry) => entry.licenceType.id),
    reference,
    expiresAt,
    attachment: held.find((entry) => entry.holding?.attachment)?.holding?.attachment ?? null,
    disagree: held.some(
      (entry) =>
        (entry.holding?.reference ?? '') !== reference ||
        dateInputValue(entry.holding?.expiresAt) !== expiresAt,
    ),
    standing: licenceStanding(
      { expires: true, warnDays },
      lead ? { expiresAt: lead.expiresAt } : null,
      now,
    ),
  }
}

/** A `yyyy-mm-dd` from a date box as the instant the API stores, or null. */
export function dateFromInput(value: string): Date | null {
  return value ? new Date(value) : null
}

/* -------------------------------------------------------------------------- */

/** The three writes, with when the last one landed and why the last one did not. */
export function useLicenceWrites() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  const options = {
    onSuccess: () => {
      setSavedAt(new Date())
      setFailure(null)
      void queryClient.invalidateQueries(trpc.licences.pathFilter())
    },
    onError: (error: unknown) => setFailure(errorMessage(error)),
  }

  return {
    record: useMutation(trpc.licences.record.mutationOptions(options)),
    remove: useMutation(trpc.licences.remove.mutationOptions(options)),
    recordCard: useMutation(trpc.licences.recordCard.mutationOptions(options)),
    savedAt,
    failure,
  }
}

export type LicenceWrites = ReturnType<typeof useLicenceWrites>

type CardDraft = {
  heldIds: string[]
  reference: string
  expiresAt: string
  attachmentId: string | null
}

/**
 * Writes the card whole through `licences.recordCard`.
 *
 * With no endorsement switched on there is no row to put a number on, so what
 * is typed waits here and goes with the first endorsement that is.
 */
export function useCardWriter(
  userId: string,
  card: TechnicianCard,
  writes: LicenceWrites,
): (patch: Partial<CardDraft>) => Promise<unknown> {
  const heldOnServer = useRef(card.heldIds)
  heldOnServer.current = card.heldIds

  return useWriteThrough<CardDraft>(
    {
      heldIds: card.heldIds,
      reference: card.reference,
      expiresAt: card.expiresAt,
      attachmentId: card.attachment?.id ?? null,
    },
    (next) => {
      if (next.heldIds.length === 0 && heldOnServer.current.length === 0) return Promise.resolve()

      return writes.recordCard.mutateAsync({
        userId,
        licenceTypeIds: next.heldIds,
        reference: next.reference || null,
        expiresAt: dateFromInput(next.expiresAt),
        attachmentId: next.attachmentId,
      })
    },
  )
}

/**
 * Writes one ticket whole through `licences.record`.
 *
 * Emptying both boxes lets the ticket go, so a row nobody holds reads "Not
 * held" again rather than "No date".
 */
export function useTicketWriter(
  userId: string,
  entry: {
    licenceType: { id: string }
    holding: { id: string; reference: string | null; expiresAt: Date | string | null; issuedAt?: Date | string | null; attachmentId?: string | null } | null
  },
  writes: LicenceWrites,
): (patch: Partial<{ reference: string; expiresAt: string }>) => Promise<unknown> {
  const holding = useRef(entry.holding)
  holding.current = entry.holding

  return useWriteThrough(
    {
      reference: entry.holding?.reference ?? '',
      expiresAt: dateInputValue(entry.holding?.expiresAt),
    },
    (next) => {
      const current = holding.current

      if (!next.reference && !next.expiresAt) {
        if (!current) return Promise.resolve()
        if (!current.issuedAt && !current.attachmentId) {
          return writes.remove.mutateAsync({ id: current.id })
        }
      }

      return writes.record.mutateAsync({
        userId,
        licenceTypeId: entry.licenceType.id,
        reference: next.reference || null,
        expiresAt: dateFromInput(next.expiresAt),
      })
    },
  )
}

/* -------------------------------------------------------------------------- */

type Undo = { label: string; run: () => void }

/** The last thing cleared, for five seconds. */
export function useUndo(): [Undo | null, (label: string, run: () => void) => void, () => void] {
  const [undo, setUndo] = useState<Undo | null>(null)
  const timer = useRef<number | null>(null)

  useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current)
  }, [])

  const dismiss = () => setUndo(null)
  const offer = (label: string, run: () => void) => {
    if (timer.current !== null) window.clearTimeout(timer.current)
    setUndo({ label, run })
    timer.current = window.setTimeout(dismiss, 5000)
  }

  return [undo, offer, dismiss]
}

export function UndoNotice({ undo, onDone }: { undo: Undo | null; onDone: () => void }) {
  if (!undo) return null

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-4">
      <div
        role="status"
        className="pointer-events-auto flex max-w-lg items-center gap-3 rounded-lg bg-ink-900 px-4 py-2 text-[13px] text-white shadow-lg"
      >
        <p className="min-w-0 truncate">{undo.label}</p>
        <Button
          size="sm"
          variant="ghost"
          className="text-brand-300 hover:bg-white/10 hover:text-brand-200"
          onClick={() => {
            undo.run()
            onDone()
          }}
        >
          Undo
        </Button>
      </div>
    </div>
  )
}

/** "Saved 2:45 pm", the failure in its place, or the promise that it will. */
export function SaveState({ writes }: { writes: LicenceWrites }) {
  if (writes.failure) {
    return <span className="text-red-700">Not saved: {writes.failure}</span>
  }

  return (
    <span>
      {writes.savedAt
        ? `Saved ${writes.savedAt.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}.`
        : 'Changes save as you type.'}
    </span>
  )
}
